import { t } from "@/localization";
import { claimNonprofitLead } from "../http/donations";
import {
  nonprofitLeadOf,
  readFundraiserIntent,
  updateFundraiserIntent,
} from "./fundraiserIntent";

// Nonprofits from eventlive.pro/nonprofit stream for free: once signed in, the
// app claims their lead on evt.live, which marks the account once Pledge has
// verified the organization. Events are unlocked when they are *created*, so
// the claim has to land before the first event is created (EventList waits
// for it before the guided fundraiser flow, CreateEventForm before saving).

export const WAIVER_GRANTED_MESSAGE = t(
  "Streaming is free for events you create before Jan 1, 2027 — thanks for fundraising with Pledge"
);
export const WAIVER_PENDING_MESSAGE = t(
  "We'll unlock free streaming as soon as Pledge confirms your organization, usually within 24 hours"
);

// How long event creation waits for a claim in progress.
export const CLAIM_WAIT_MS = 12000;
const NETWORK_RETRY_DELAY_MS = 1500;
// A pending claim is asked again after this, so the note turns into the
// granted one once Pledge approves (evt.live grants on its webhook).
const PENDING_RECHECK_MS = 10 * 60 * 1000;
// Unknown, malformed, stale or someone else's lead: asking again can't help.
const FINAL_STATUSES = [400, 404, 409, 410];

let inflight = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function claimOnce(leadId, claim) {
  try {
    const data = await claim(leadId);
    return {
      status: typeof data?.status === "string" ? data.status : "unknown",
      until: typeof data?.until === "string" ? data.until : "",
      at: Date.now(),
    };
  } catch (error) {
    if (FINAL_STATUSES.includes(error?.status)) {
      return { status: "rejected", code: error.status, at: Date.now() };
    }
    throw error;
  }
}

const isSettled = (claim, now) =>
  !!claim &&
  !(
    claim.status === "pending_verification" &&
    now - (claim.at || 0) > PENDING_RECHECK_MS
  );

// Claims the lead of the stored fundraiser intent for the signed-in
// broadcaster, once. Resolves to evt.live's answer ({ status, until }), or
// null when there is nothing to claim or it can't be done right now (signed
// out, offline, server error: tried again next time). Never rejects.
export function ensureNonprofitWaiverClaim({
  claim = claimNonprofitLead,
  retryDelayMs = NETWORK_RETRY_DELAY_MS,
} = {}) {
  if (inflight) return inflight;
  const run = (async () => {
    try {
      const intent = await readFundraiserIntent();
      const leadId = nonprofitLeadOf(intent);
      if (!leadId) return null;
      if (isSettled(intent.waiverClaim, Date.now())) return intent.waiverClaim;

      let result;
      try {
        result = await claimOnce(leadId, claim);
      } catch (error) {
        // Only network failures (status 0) are retried right away.
        if (error?.status !== 0) return null;
        await delay(retryDelayMs);
        result = await claimOnce(leadId, claim);
      }
      await updateFundraiserIntent({ waiverClaim: result });
      return result;
    } catch (error) {
      return null;
    } finally {
      if (inflight === run) inflight = null;
    }
  })();
  inflight = run;
  return run;
}

// Before creating an event: wait (bounded) for the claim, starting it if
// needed. Never rejects and never blocks longer than timeoutMs.
export async function waitForNonprofitWaiverClaim({
  timeoutMs = CLAIM_WAIT_MS,
  ...options
} = {}) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  try {
    return await Promise.race([ensureNonprofitWaiverClaim(options), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

// The one line shown where events are scheduled; empty for anything else.
export const nonprofitWaiverMessage = (claim, now = Date.now()) => {
  if (claim?.status === "granted") {
    const until = Date.parse(claim.until);
    return Number.isFinite(until) && until <= now ? "" : WAIVER_GRANTED_MESSAGE;
  }
  return claim?.status === "pending_verification" ? WAIVER_PENDING_MESSAGE : "";
};

// Test helper.
export const __resetNonprofitWaiverClaim = () => {
  inflight = null;
};
